import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { ScrollProgressBar } from "./ScrollProgressBar";
import { LiveMetricsSection } from "./LiveMetricsSection";
import { TechStackMarquee } from "./TechStackMarquee";
import { ComparisonTableSection } from "./ComparisonTableSection";
import { Navbar } from "../Basic/NavBar";
import { HeroSection } from "./HeroSection";
import { Services } from "./Services";
import { Footer } from "../Basic/Footer";
import { WordMark } from "../Basic/WordMark";
import { SatpalSection } from "./SatpalSection";

export default function ReadalSystems() {
  const glowRef = useRef(null);
  const whyCanvasRef = useRef(null);
  const rootRef = useRef(null);

  // Cursor glow
  useEffect(() => {
    const glow = glowRef.current;
    const onMove = (e) => {
      if (!glow) return;
      glow.style.left = e.clientX + "px";
      glow.style.top = e.clientY + "px";
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  // Magnetic buttons
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const btns = root.querySelectorAll(".magnetic");
    const handlers = [];
    btns.forEach((btn) => {
      const onMove = (e) => {
        const r = btn.getBoundingClientRect();
        const x = e.clientX - r.left - r.width / 2;
        const y = e.clientY - r.top - r.height / 2;
        btn.style.transform = `translate(${x * 0.18}px, ${y * 0.35}px) scale(1.04)`;
      };
      const onLeave = () => {
        btn.style.transform = "";
      };
      btn.addEventListener("mousemove", onMove);
      btn.addEventListener("mouseleave", onLeave);
      handlers.push([btn, onMove, onLeave]);
    });
    return () => {
      handlers.forEach(([btn, onMove, onLeave]) => {
        btn.removeEventListener("mousemove", onMove);
        btn.removeEventListener("mouseleave", onLeave);
      });
    };
  }, []);

  // Scroll reveal
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const els = root.querySelectorAll(".reveal, .tl-item");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) en.target.classList.add("in");
        });
      },
      { threshold: 0.15 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // Count-up stats
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const counters = root.querySelectorAll(".count");
    const countIO = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            const el = en.target;
            const target = +el.dataset.target;
            let cur = 0;
            const step = Math.max(1, target / 60);
            const tick = () => {
              cur += step;
              if (cur >= target) {
                el.textContent = target;
              } else {
                el.textContent = Math.floor(cur);
                requestAnimationFrame(tick);
              }
            };
            tick();
            countIO.unobserve(el);
          }
        });
      },
      { threshold: 0.4 }
    );
    counters.forEach((c) => countIO.observe(c));
    return () => countIO.disconnect();
  }, []);

  // "Why Readal" mini 3D
  useEffect(() => {
    const canvas = whyCanvasRef.current;
    if (!canvas) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
    camera.position.set(0, 0, 5);
    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });

    const resize = () => {
      const s = canvas.parentElement.getBoundingClientRect();
      renderer.setSize(s.width, s.height);
      camera.aspect = s.width / s.height;
      camera.updateProjectionMatrix();
    };
    resize();
    window.addEventListener("resize", resize);

    const group = new THREE.Group();
    const cube = new THREE.Mesh(
      new THREE.BoxGeometry(1.3, 1.3, 1.3),
      new THREE.MeshBasicMaterial({ color: 0x1da1f2, wireframe: true, transparent: true, opacity: 0.6 })
    );
    group.add(cube);
    for (let i = 0; i < 3; i++) {
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(1.6 + i * 0.4, 0.005, 8, 90),
        new THREE.MeshBasicMaterial({ color: 0x49d6ff, transparent: true, opacity: 0.3 })
      );
      ring.rotation.x = Math.random() * Math.PI;
      ring.rotation.y = Math.random() * Math.PI;
      group.add(ring);
    }
    scene.add(group);

    let frameId;
    const animate = () => {
      frameId = requestAnimationFrame(animate);
      group.rotation.y += 0.004;
      group.rotation.x += 0.0015;
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", resize);
      renderer.dispose();
    };
  }, []);

  return (
    <div ref={rootRef} className="readal-root">


      <div id="bg-grid"></div>
      <div id="noise"></div>
      <div className="cursor-glow" ref={glowRef}></div>

      <Navbar />

      <ScrollProgressBar />
      
      {/* HERO */}
      <HeroSection />

      {/* TRUSTED BY */}
      <div className="trustbar">
        <div className="container trust-row reveal">
          <span>VERTEXA</span>
          <span>NIMBUS&nbsp;CO</span>
          <span>ORBITAL</span>
          <span>HALCYON</span>
          <span>FORGEWORKS</span>
          <span>QUANTRA</span>
        </div>
      </div>

      {/* SERVICES */}
      <Services />

      {/* Tech Stack */}
      <TechStackMarquee />
      
      {/* SOLUTIONS / INDUSTRIES */}
      <section id="solutions" style={{ background: "var(--navy)" }}>
        <div className="container">
          <div className="section-head reveal">
            <div className="eyebrow">Industries</div>
            <h2>Built for the businesses that can&apos;t afford to wait.</h2>
          </div>
          <div className="grid-3">
            <div className="card reveal">
              <div className="icon-box">
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" />
                </svg>
              </div>
              <h3>Manufacturing &amp; Retail</h3>
              <p>Inventory, procurement, and floor operations unified under one automated ERP backbone.</p>
            </div>
            <div className="card reveal delay-1">
              <div className="icon-box">
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                </svg>
              </div>
              <h3>Healthcare &amp; Logistics</h3>
              <p>Mission-critical pipelines for scheduling, tracking, and compliance, automated end to end.</p>
            </div>
            <div className="card reveal delay-2">
              <div className="icon-box">
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 1v22M5 8l7-7 7 7M5 16l7 7 7-7" />
                </svg>
              </div>
              <h3>Finance &amp; SaaS</h3>
              <p>Real-time dashboards and AI-assisted decisioning, wired straight into your existing stack.</p>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS TIMELINE */}
      <section id="process" style={{ background: "var(--navy)" }}>
        <div className="container">
          <div className="section-head reveal">
            <div className="eyebrow">How we work</div>
            <h2>From audit to autopilot.</h2>
          </div>
          <div className="timeline">
            <div className="tl-item reveal">
              <div className="tl-dot">01</div>
              <h4>Audit &amp; Map</h4>
              <p>We trace how work actually moves through your business, not how the org chart says it should.</p>
            </div>
            <div className="tl-item reveal delay-1">
              <div className="tl-dot">02</div>
              <h4>Design the System</h4>
              <p>Architecture, data flow, and AI touchpoints are mapped before any build begins.</p>
            </div>
            <div className="tl-item reveal delay-2">
              <div className="tl-dot">03</div>
              <h4>Build &amp; Integrate</h4>
              <p>ERP, automation, and interface ship together, tested against real operational data.</p>
            </div>
            <div className="tl-item reveal delay-3">
              <div className="tl-dot">04</div>
              <h4>Launch &amp; Tune</h4>
              <p>We stay on after go-live, tuning models and flows against real usage.</p>
            </div>
          </div>
        </div>
      </section>

      {/* WHY READAL */}
      <section id="why">
        <div className="container">
          <div className="why-wrap">
            <div className="reveal">
              <div className="eyebrow">Why Readal</div>
              <h2 style={{ margin: "18px 0 32px", fontSize: "clamp(28px,3.4vw,38px)" }}>
                Most agencies ship features.
                <br />
                We ship systems that compound.
              </h2>
              <div className="why-list">
                <div className="why-item">
                  <div className="why-num">01</div>
                  <div>
                    <h4>Precision over speed-for-its-own-sake</h4>
                    <p>Every automation is mapped to a measurable outcome before a line of code ships.</p>
                  </div>
                </div>
                <div className="why-item">
                  <div className="why-num">02</div>
                  <div>
                    <h4>One team, the full stack</h4>
                    <p>AI, ERP, web, and cloud sit under one roof — no fragmented vendor chain.</p>
                  </div>
                </div>
                <div className="why-item">
                  <div className="why-num">03</div>
                  <div>
                    <h4>Built to evolve</h4>
                    <p>Architecture designed to absorb new models and integrations without a rebuild.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="why-visual reveal delay-1">
              <canvas id="why-canvas" ref={whyCanvasRef}></canvas>
            </div>
          </div>
        </div>
      </section>

      <SatpalSection />

      {/* STATS */}
      <section id="stats">
        <div className="container">

          {/* Live Metrics */}
          <LiveMetricsSection />

          <div className="stats-wrap reveal">
            <div className="stat">
              <div className="num mono"><span className="count" data-target="120">0</span>+</div>
              <div className="label">SYSTEMS SHIPPED</div>
            </div>
            <div className="stat">
              <div className="num mono"><span className="count" data-target="98">0</span>%</div>
              <div className="label">UPTIME SLA</div>
            </div>
            <div className="stat">
              <div className="num mono"><span className="count" data-target="40">0</span>%</div>
              <div className="label">AVG. OPS TIME SAVED</div>
            </div>
            <div className="stat">
              <div className="num mono"><span className="count" data-target="24">0</span>/7</div>
              <div className="label">MONITORING</div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonials" style={{ background: "var(--navy)" }}>
        <div className="container">
          <div className="section-head reveal">
            <div className="eyebrow">In their words</div>
            <h2>
              Trusted by the teams
              <br />
              who automated first.
            </h2>
          </div>
          <div className="grid-3">
            <div className="testi-card reveal">
              <p>
                &quot;Readal didn&apos;t just connect our ERP and AI tools — they redesigned how the two
                actually talk to each other. Order-to-cash dropped from days to hours.&quot;
              </p>
              <div className="testi-person">
                <div className="avatar"></div>
                <div>
                  <div className="name">Maya Lindqvist</div>
                  <div className="role">COO, Nimbus Co.</div>
                </div>
              </div>
            </div>
            <div className="testi-card reveal delay-1">
              <p>&quot;The automation layer they built now handles work that used to take three people a full week. It just runs.&quot;</p>
              <div className="testi-person">
                <div className="avatar"></div>
                <div>
                  <div className="name">Daniel Osei</div>
                  <div className="role">Head of Ops, Vertexa</div>
                </div>
              </div>
            </div>
            <div className="testi-card reveal delay-2">
              <p>&quot;Most vendors hand you a dashboard and disappear. Readal stayed, tuned the models, and kept shipping.&quot;</p>
              <div className="testi-person">
                <div className="avatar"></div>
                <div>
                  <div className="name">Priya Raman</div>
                  <div className="role">CTO, Forgeworks</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      < ComparisonTableSection/>

      {/* GIANT WORDMARK */}
      <WordMark />

      {/* FOOTER */}
      <Footer />

    </div>
  );
}